import {
  Adapter,
  AdapterState,
  WalletReadyState
} from '@tronweb3/tronwallet-abstract-adapter'
import {
  BitKeepAdapter,
  GateWalletAdapter,
  ImTokenAdapter,
  OkxWalletAdapter,
  TokenPocketAdapter,
  TronLinkAdapter,
  FoxWalletAdapter,
  BybitWalletAdapter
} from '@tronweb3/tronwallet-adapters'
import { EventEmitter } from 'eventemitter3'
import { formatUnits, parseUnits } from 'ethers'
import { TronWeb } from 'tronweb'
import { trc20Abi } from './abi'
import { makeAppkitError, TronWalletError } from './error'

export const ChainId = {
  tron: '0x2b6653dc',
  shasta: '0x94a9059e',
  nile: '0xcd8690dc'
} as const

export const FullNode = {
  tron: 'https://api.tronstack.io',
  shasta: 'https://api.shasta.trongrid.io',
  nile: 'https://nile.trongrid.io'
}

const tronWeb = new TronWeb({
  fullHost: FullNode.nile,
  privateKey: ''
})

export type Base58 = `T${string}`

export type NetworkName = keyof typeof ChainId

export type AccountState = {
  address: '' | Base58
  status: Status
  isConnected: boolean
}

const isAccountEqual = (a: AccountState, b: AccountState) =>
  a.address === b.address && a.status === b.status

export type State = {
  adapters: Adapter[]
  adapter: Adapter | undefined
  network: '' | NetworkName
  address: '' | Base58
  status: Status

  isOpen: boolean
}

export enum Status {
  /**
   * If adapter is checking the wallet, the state is Loading.
   */
  Loading = 'loading',
  /**
   * If wallet is not installed, the state is NotFound.
   */
  NotFound = 'notFound',
  /**
   * If wallet is installed but is not connected to current Dapp, the state is Disconnected.
   */
  Disconnected = 'disconnected',
  /**
   * Wallet is connected to current Dapp.
   */
  Connected = 'connected',
  /**
   * Wallet is connecting to current Dapp.
   */
  Connecting = 'connecting'
}

export type TronAppkit = {
  getSnapshot(): State
  setAdapter(v: Adapter | undefined): void
  getAccount(): AccountState
  onAccount(callback: (v: AccountState) => void): () => void

  getNetwork(): NetworkName | ''
  setNetwork(networkName: NetworkName): Promise<void>
  connect(): Promise<void>
  connectByAdapter(v: Adapter): Promise<void>
  disconnect(): Promise<void>
  signMessage(params: { message: string }): Promise<Base58>
  trc20BalanceOf(params: {
    contractAddress: Base58
    decimals?: number
    account?: Base58
  }): Promise<string>
  trc20Transfer(params: {
    contractAddress: Base58
    recipient: Base58
    amount: string
    decimals?: number
  }): Promise<Base58>

  open(): void
  setOpen(v: boolean): void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dummyFn: any = () => {
  throw new Error('TronAppkit not found. Please call makeTronAppkit first.')
}
const dummyState: State = {
  adapters: [],
  adapter: undefined,
  network: '',
  address: '',
  status: Status.NotFound,
  isOpen: false
}
const NullStoreValue: TronAppkit = {
  getSnapshot: () => {
    console.warn('TronAppkit not found. Please call makeTronAppkit first.')
    return dummyState
  },
  setAdapter: dummyFn,
  getAccount: dummyFn,
  onAccount: dummyFn,

  getNetwork: dummyFn,
  setNetwork: dummyFn,
  connect: dummyFn,
  connectByAdapter: dummyFn,
  disconnect: dummyFn,
  signMessage: dummyFn,
  trc20BalanceOf: dummyFn,
  trc20Transfer: dummyFn,

  open: dummyFn,
  setOpen: dummyFn
}

export const Store = {
  _channel: new EventEmitter(),
  _value: NullStoreValue,
  get value() {
    return Store._value
  },
  set value(v: TronAppkit) {
    Store._value = v
    Store._channel.emit('STATE')
  },
  on(callback: () => void): () => void {
    Store._channel.on('STATE', callback)
    return () => {
      Store._channel.off('STATE', callback)
    }
  },
  emit() {
    if (Store._value !== NullStoreValue) {
      Store._value = { ...Store._value }
    }
    Store._channel.emit('STATE')
  }
}

export function makeTronAppkit(): TronAppkit | null {
  if (Store.value !== NullStoreValue) return Store.value

  const stateDefault: State = {
    // TODO watch adapter state and readyState
    adapters: [
      new TronLinkAdapter(),
      new TokenPocketAdapter(),
      new OkxWalletAdapter(),
      new BitKeepAdapter(),
      new GateWalletAdapter(),
      new ImTokenAdapter(),
      new FoxWalletAdapter(),
      new BybitWalletAdapter()
      // new LedgerAdapter()
      // new WalletConnectAdapter(walletconnectConfig)
    ],
    adapter: undefined,
    network: '',
    address: '',
    status: Status.NotFound,
    isOpen: false
  }
  let state: State = {
    ...stateDefault
  }

  const patchState = (v: Partial<State> | undefined): void => {
    const nextState = v ? { ...state, ...v } : { ...stateDefault }
    console.debug({ name: 'tron patchState', v, state, nextState })

    // must sync tronWeb for contract interactive
    if (nextState.address && nextState.address != state.address)
      tronWeb.setAddress(nextState.address)
    if (nextState.network != state.network)
      tronWeb.setFullNode(
        FullNode[nextState.network as NetworkName] ?? FullNode.nile
      )

    state = nextState
    Store.emit()
  }

  const getAccount: TronAppkit['getAccount'] = () => ({
    address: state.address,
    status: state.status,
    isConnected: state.status === Status.Connected
  })

  function onReadyStateChanged(readyState: WalletReadyState) {
    patchState({
      status: toStatus(
        state.adapter ? { ...state.adapter, readyState } : undefined
      ),
      address: (state.adapter?.address as Base58) || ''
    })
  }

  function onConnect() {
    patchState({
      status: toStatus(state.adapter),
      address: (state.adapter?.address as Base58) || ''
    })
    ;(state.adapter as TronLinkAdapter)
      ?.network?.()
      .then(network => patchState({ network: toNetwork(network.chainId) }))
  }

  function onAccountsChanged(account: string) {
    patchState({
      address: account as Base58
    })
  }

  function onDisconnect() {
    patchState({
      status: Status.Disconnected,
      address: ''
    })
  }

  function onChainChanged(chainData: unknown) {
    patchState({
      network: toNetwork((chainData as { chainId: string }).chainId)
    })
  }

  state.adapters.forEach(v =>
    v.once('readyStateChanged', () => {
      patchState({ adapters: state.adapters.slice() })
    })
  )

  Store.value = {
    getSnapshot() {
      return state
    },

    setAdapter(v) {
      if (state.adapter === v) return

      if (state.adapter) state.adapter.removeAllListeners()

      patchState({
        adapter: v,
        address: (v?.address as Base58) || '',
        status: toStatus(v)
      })

      if (state.adapter) {
        state.adapter.on('readyStateChanged', onReadyStateChanged)
        state.adapter.on('connect', onConnect)
        state.adapter.on('accountsChanged', onAccountsChanged)
        state.adapter.on('disconnect', onDisconnect)
        state.adapter.on('chainChanged', onChainChanged)
      }

      if (state.adapter?.connected) {
        ;(state.adapter as TronLinkAdapter)
          ?.network()
          .then(network => patchState({ network: toNetwork(network.chainId) }))
      }

      Store.emit()
    },

    getAccount,

    onAccount(callback) {
      let before = getAccount()
      callback(before)

      const cb = () => {
        const after = getAccount()
        if (isAccountEqual(before, after)) return

        before = after
        callback(after)
      }
      return Store.on(cb)
    },

    getNetwork() {
      return state.network
    },

    async setNetwork(networkName) {
      await state.adapter?.switchChain(ChainId[networkName]).catch(e => {
        console.warn(e)
      })
      patchState({ network: networkName })
    },

    async connect() {
      if (state.status === Status.Connected) return

      patchState({ isOpen: true })

      return new Promise((resolve, reject) => {
        Store._channel.once('CONNECT', e => {
          return e instanceof Error ? reject(e) : resolve()
        })

        const unsubscribeCancel = Store.on(() => {
          if (state.isOpen) return
          unsubscribeCancel()
          const cancelledError = makeAppkitError({
            name: 'WalletNotSelectedError',
            message: 'User cancelled',
            error: null
          } satisfies TronWalletError)
          Store._channel.emit('CONNECT', cancelledError)
        })
      })
    },

    async connectByAdapter(v: Adapter) {
      if (!v) return

      Store.value.setAdapter(v)

      if (state.status === Status.Connected) {
        Store._channel.emit('CONNECT')
        patchState({ isOpen: false })
        return
      }

      patchState({ status: Status.Connecting })

      try {
        await v.connect()
        patchState({
          status: Status.Connected,
          address: (v.address as Base58) || ''
        })
        Store._channel.emit('CONNECT')
      } catch (e) {
        patchState({
          status: toStatus(v ? { ...v, connecting: false } : undefined)
        })
        Store._channel.emit('CONNECT', makeAppkitError(e))
      } finally {
        patchState({ isOpen: false })
      }
    },

    async disconnect() {
      try {
        await state.adapter?.disconnect()
        patchState({
          status: Status.Disconnected,
          address: ''
        })
      } catch (error) {
        throw makeAppkitError(error)
      }
    },

    async signMessage({ message }) {
      if (!state.adapter || !state.adapter.address) {
        throw makeAppkitError({
          name: 'WalletNotSelectedError',
          message: 'no connect',
          error: null
        } satisfies TronWalletError)
      }

      try {
        const txHash = (await state.adapter.signMessage(message)) as Base58
        return txHash
      } catch (error) {
        throw makeAppkitError(error)
      }
    },

    async trc20BalanceOf({ contractAddress, decimals, account }) {
      const address = account ?? state.address

      if (!state.adapter) {
        throw makeAppkitError({
          name: 'WalletNotSelectedError',
          message: 'no connect',
          error: null
        } satisfies TronWalletError)
      }
      if (!address) {
        throw makeAppkitError({
          name: 'WalletNotSelectedError',
          message: 'account is required',
          error: null
        } satisfies TronWalletError)
      }

      try {
        const contract = await tronWeb.contract(trc20Abi, contractAddress)
        const _decimals =
          typeof decimals === 'number'
            ? decimals
            : ((await contract.decimals().call()) as number)
        const result = await contract.balanceOf(address).call()
        const value = formatUnits(result.toString(), _decimals)

        return value
      } catch (error) {
        // TODO 校正 contractAddress 給錯時收到 Error. Smart contract is missing address
        throw makeAppkitError(error)
      }
    },

    async trc20Transfer({ contractAddress, recipient, amount, decimals }) {
      if (
        !state.adapter ||
        !state.address ||
        state.status !== Status.Connected
      ) {
        throw makeAppkitError({
          name: 'WalletNotSelectedError',
          message: 'account is required',
          error: null
        } satisfies TronWalletError)
      }

      try {
        const contract = await tronWeb.contract(trc20Abi, contractAddress)
        const _decimals =
          typeof decimals === 'number'
            ? decimals
            : ((await contract.decimals().call()) as number)
        const tx = await tronWeb.transactionBuilder.triggerSmartContract(
          contractAddress,
          'transfer(address,uint256)',
          {},
          [
            { type: 'address', value: recipient },
            { type: 'uint256', value: parseUnits(amount, _decimals).toString() }
          ]
        )
        const signedTx = await state.adapter.signTransaction(tx.transaction)
        const result = await tronWeb.trx.sendRawTransaction(signedTx)

        return result.txid as Base58
      } catch (error) {
        // TODO 校正 contractAddress 給錯時收到 Error. Smart contract is missing address
        throw makeAppkitError(error)
      }
    },

    open() {
      patchState({ isOpen: true })
    },

    setOpen(v) {
      patchState({ isOpen: v })
    }
  }

  return Store.value
}

function toNetwork(chainId: string) {
  return (
    (Object.entries(ChainId).find(([, id]) => id === chainId)?.[0] as
      | NetworkName
      | undefined) ?? ''
  )
}

function toStatus(
  adapter: Pick<Adapter, 'readyState' | 'connecting' | 'state'> | undefined
) {
  if (!adapter) return Status.NotFound

  const { readyState, connecting, state } = adapter

  return readyState === WalletReadyState.Found
    ? connecting
      ? Status.Connecting
      : ({
          [AdapterState.Loading]: Status.Loading,
          [AdapterState.NotFound]: Status.NotFound,
          [AdapterState.Disconnect]: Status.Disconnected,
          [AdapterState.Connected]: Status.Connected
        }[state as AdapterState] ?? Status.NotFound)
    : Status.NotFound
}
